import React from 'react';
import { motion } from 'framer-motion';
import { Loader, Send, Trash2, ImagePlus } from 'lucide-react';
import { categoryLabels, ForumThreadRow } from './forumLabels';

type Reply = {
  _id: string;
  content: string;
  createdAt: string;
  imageUrls?: string[];
  author?: { name: string };
};

type Props = {
  thread: ForumThreadRow;
  replies: Reply[];
  loading: boolean;
  isLoggedIn: boolean;
  canDelete: boolean;
  submitting: boolean;
  replyContent: string;
  replyFiles: File[];
  onReplyContentChange: (v: string) => void;
  onReplyFilesChange: (files: File[]) => void;
  onReply: () => void;
  onDelete: () => void;
};

const ForumThreadDetail: React.FC<Props> = ({
  thread,
  replies,
  loading,
  isLoggedIn,
  canDelete,
  submitting,
  replyContent,
  replyFiles,
  onReplyContentChange,
  onReplyFilesChange,
  onReply,
  onDelete,
}) => {
  if (loading) {
    return (
      <div className="forum-loading">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
          <Loader size={32} color="var(--accent-primary)" />
        </motion.div>
      </div>
    );
  }

  return (
    <div>
      <div className="forum-detail-card glow-card">
        <div className="forum-detail-card__head">
          <div>
            <span className="forum-badge">{categoryLabels[thread.category] || thread.category}</span>
            <h1>{thread.title}</h1>
            <div className="forum-detail-card__meta">
              {thread.author?.name || 'Üye'} · {new Date(thread.createdAt).toLocaleString('tr-TR')}
            </div>
          </div>
          {canDelete && (
            <button type="button" className="forum-btn forum-btn--danger-outline" onClick={onDelete}>
              <Trash2 size={14} /> Sil
            </button>
          )}
        </div>
        <p className="forum-detail-card__body">{thread.content}</p>
        {thread.imageUrls && thread.imageUrls.length > 0 && (
          <div className="forum-detail-card__images">
            {thread.imageUrls.map((u) => (
              <a key={u} href={u} target="_blank" rel="noreferrer">
                <img src={u} alt="" />
              </a>
            ))}
          </div>
        )}
      </div>

      <h2 className="forum-replies-title">Yanıtlar ({replies.length})</h2>
      <div className="forum-replies">
        {replies.map((r) => (
          <div key={r._id} className="forum-reply-card glow-card">
            <div className="forum-reply-card__meta">
              {r.author?.name || 'Üye'} · {new Date(r.createdAt).toLocaleString('tr-TR')}
            </div>
            <p>{r.content}</p>
            {r.imageUrls && r.imageUrls.length > 0 && (
              <div className="forum-reply-card__images">
                {r.imageUrls.map((u) => (
                  <img key={u} src={u} alt="" />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="forum-reply-form glow-card">
        <textarea
          value={replyContent}
          onChange={(e) => onReplyContentChange(e.target.value)}
          placeholder={isLoggedIn ? 'Yanıtınızı yazın…' : 'Yanıtlamak için giriş yapın'}
          disabled={!isLoggedIn}
          rows={4}
        />
        <div className="forum-reply-form__actions">
          <label className="forum-file-label">
            <ImagePlus size={16} /> Görsel
            <input
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => onReplyFilesChange(Array.from(e.target.files || []).slice(0, 4))}
            />
          </label>
          {replyFiles.length > 0 && <span className="forum-file-hint">{replyFiles.length} dosya</span>}
          <motion.button
            type="button"
            whileTap={{ scale: 0.98 }}
            disabled={submitting}
            className="forum-btn forum-btn--primary"
            onClick={onReply}
          >
            {submitting ? <Loader size={16} /> : <Send size={16} />}
            Gönder
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default ForumThreadDetail;
